import { RotateCcw, Shield } from "lucide-react";
import { useEffect, useRef, useState } from "react";

type Box = { x: number; y: number; width: number; height: number };

async function flatten(imageUrl: string, boxes: Box[]): Promise<Blob> {
  const image = new Image();
  image.src = imageUrl;
  await image.decode();
  const canvas = document.createElement("canvas");
  canvas.width = image.naturalWidth;
  canvas.height = image.naturalHeight;
  const context = canvas.getContext("2d");
  if (!context) throw new Error("Canvas is unavailable.");
  context.drawImage(image, 0, 0);
  context.fillStyle = "#0f172a";
  boxes.forEach((box) => {
    context.fillRect(
      box.x * canvas.width,
      box.y * canvas.height,
      box.width * canvas.width,
      box.height * canvas.height,
    );
  });
  return new Promise((resolve, reject) =>
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error("Could not prepare screenshot."))),
      "image/png",
    ),
  );
}

async function crop(imageUrl: string, box: Box): Promise<Blob> {
  const image = new Image();
  image.src = imageUrl;
  await image.decode();
  const sourceX = Math.round(box.x * image.naturalWidth);
  const sourceY = Math.round(box.y * image.naturalHeight);
  const width = Math.max(1, Math.round(box.width * image.naturalWidth));
  const height = Math.max(1, Math.round(box.height * image.naturalHeight));
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  canvas.getContext("2d")?.drawImage(image, sourceX, sourceY, width, height, 0, 0, width, height);
  return new Promise((resolve, reject) =>
    canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error("Could not crop screenshot."))), "image/png"),
  );
}

export function ScreenshotRedactor({
  source,
  onPrepared,
}: {
  source: Blob;
  onPrepared: (blob: Blob) => void;
}) {
  const [url, setUrl] = useState("");
  const [displayBlob, setDisplayBlob] = useState(source);
  const [boxes, setBoxes] = useState<Box[]>([]);
  const [draft, setDraft] = useState<Box | null>(null);
  const [mode, setMode] = useState<"hide" | "crop">("hide");
  const start = useRef<{ x: number; y: number } | null>(null);
  const stage = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    setDisplayBlob(source);
    setBoxes([]);
    onPrepared(source);
  }, [source, onPrepared]);

  useEffect(() => {
    const next = URL.createObjectURL(displayBlob);
    setUrl(next);
    return () => URL.revokeObjectURL(next);
  }, [displayBlob]);

  function point(event: React.PointerEvent) {
    const bounds = stage.current!.getBoundingClientRect();
    return {
      x: Math.max(0, Math.min(1, (event.clientX - bounds.left) / bounds.width)),
      y: Math.max(0, Math.min(1, (event.clientY - bounds.top) / bounds.height)),
    };
  }

  function onPointerDown(event: React.PointerEvent) {
    event.currentTarget.setPointerCapture(event.pointerId);
    start.current = point(event);
  }

  function onPointerMove(event: React.PointerEvent) {
    if (!start.current) return;
    const current = point(event);
    setDraft({
      x: Math.min(start.current.x, current.x),
      y: Math.min(start.current.y, current.y),
      width: Math.abs(current.x - start.current.x),
      height: Math.abs(current.y - start.current.y),
    });
  }

  async function onPointerUp() {
    if (!draft || draft.width < 0.01 || draft.height < 0.01) {
      start.current = null;
      setDraft(null);
      return;
    }
    if (mode === "crop") {
      let cropUrl = url;
      let temporaryUrl: string | null = null;
      if (boxes.length) {
        temporaryUrl = URL.createObjectURL(await flatten(url, boxes));
        cropUrl = temporaryUrl;
      }
      const cropped = await crop(cropUrl, draft);
      if (temporaryUrl) URL.revokeObjectURL(temporaryUrl);
      setDisplayBlob(cropped);
      setBoxes([]);
      setDraft(null);
      start.current = null;
      setMode("hide");
      onPrepared(cropped);
      return;
    }
    const next = [...boxes, draft];
    setBoxes(next);
    setDraft(null);
    start.current = null;
    onPrepared(await flatten(url, next));
  }

  async function undo() {
    const next = boxes.slice(0, -1);
    setBoxes(next);
    onPrepared(next.length ? await flatten(url, next) : source);
  }

  function reset() {
    setDisplayBlob(source);
    setBoxes([]);
    setMode("hide");
    onPrepared(source);
  }

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="flex items-center gap-1.5 text-[11px] text-slate-500"><Shield className="h-3.5 w-3.5" /> {mode === "crop" ? "Drag around the area to keep." : "Drag over private details to hide them permanently."}</p>
        <div className="flex gap-1">
          <button type="button" onClick={() => setMode((value) => value === "crop" ? "hide" : "crop")} className={`rounded px-2 py-1 text-[10.5px] font-medium ${mode === "crop" ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600"}`}>Crop</button>
          <button type="button" onClick={() => void undo()} disabled={boxes.length === 0} className="inline-flex items-center gap-1 rounded px-2 py-1 text-[10.5px] font-medium text-slate-600 disabled:opacity-40"><RotateCcw className="h-3 w-3" /> Undo hide</button>
          <button type="button" onClick={reset} className="rounded px-2 py-1 text-[10.5px] font-medium text-slate-600">Reset</button>
        </div>
      </div>
      <div
        ref={stage}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={() => void onPointerUp()}
        className="relative max-h-64 cursor-crosshair select-none overflow-hidden rounded-lg border border-slate-200 bg-slate-100"
      >
        {url && <img src={url} alt="Screenshot preview" className="block h-auto max-h-64 w-full object-contain" draggable={false} />}
        {[...boxes, ...(draft ? [draft] : [])].map((box, index) => (
          <span
            key={index}
            className={`absolute ${draft === box && mode === "crop" ? "border-2 border-white bg-slate-950/20" : "bg-slate-950"}`}
            style={{
              left: `${box.x * 100}%`,
              top: `${box.y * 100}%`,
              width: `${box.width * 100}%`,
              height: `${box.height * 100}%`,
            }}
          />
        ))}
      </div>
    </div>
  );
}
